import { useState, useEffect, useCallback } from "react"
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
} from "chart.js"
import ChartDataLabels from "chartjs-plugin-datalabels"
import { Doughnut, Bar } from "react-chartjs-2"
import axios from "axios"
import getTimeZoneDateRange from "../../utils/getTimeZoneDateRange"

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartDataLabels
)

// =============================================================================
// CONSTANTS
// =============================================================================
const SCREEN_LABELS = {
  landing: "Landing Page",
  landing_video: "Watched Landing Video",
  safety_screen: "Safety Screen",
  problem_area: "Problem Area",
  pain_location: "Pain Location",
  cpg_questions: "CPG Questions",
  subtype_questioning: "Subtype Questions",
  self_assessment: "Self Assessment",
  nprs_symptoms: "NPRS/Symptoms",
  test1_demo: "Demo Video 1",
  camera_preview: "Camera Preview",
  test1_ar: "Movement Test 1",
  test1_rpe: "RPE",
  test1_symptoms: "Symptoms (Test 1)",
  test2_demo: "Demo Video 2",
  test2_ar: "Movement Test 2",
  test2_symptoms: "Symptoms (Test 2)",
  personalize: "Demographics",
  recovery_score: "Recovery Score",
  plan_screen: "Plan Screen",
}

const TOUCHPOINT_LABELS = {
  landing: 'Landing Page',
  post_plan: 'Post-Assessment',
  post_session: 'In-App (Post-Session)'
}

// Helper function to format duration in seconds
const formatDuration = (seconds) => {
  if (!seconds && seconds !== 0) return "—"
  if (seconds < 60) return `${seconds}s`
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`
}

// Helper to format date only (e.g., "Dec 18, 2025")
const formatDate = (date) => {
  if (!date) return "—"
  // If date-only string (YYYY-MM-DD), parse parts directly to avoid UTC timezone shift
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const [year, month, day] = date.split('-').map(Number)
    const dt = new Date(year, month - 1, day) // month is 0-indexed
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }
  const dt = new Date(date)
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

// Helper function to format ISO date-time string (e.g., "Dec 18, 10:40 PM")
const formatDateTime = (date) => {
  if (!date) return "—"
  const dt = new Date(date)
  const month = dt.toLocaleString('en-US', { month: 'short' })
  const day = dt.getDate()
  const hour = dt.getHours()
  const minute = dt.getMinutes().toString().padStart(2, '0')
  const hour12 = hour % 12 || 12
  const ampm = hour >= 12 ? 'PM' : 'AM'
  return `${month} ${day}, ${hour12}:${minute} ${ampm}`
}

// Helper function to format exit reasons
const formatExitReason = (reason) => {
  if (!reason) return null
  const mapping = {
    camera_permission_denied: "Camera denied",
    ar_init_failed: "AR failed",
    user_skipped: "Skipped",
    timeout: "Timeout",
    browser_not_supported: "Browser unsupported",
    device_not_supported: "Device unsupported",
    error_unknown: "Error",
    abandoned: "Abandoned",
  }
  return mapping[reason] || reason.replace(/_/g, ' ')
}

// Helper to format user status
const formatUserStatus = (status) => {
  const mapping = {
    "Signed up": { bg: "bg-blue-100", text: "text-blue-700" },
    "Completed GSA": { bg: "bg-green-100", text: "text-green-700" },
    "Started GSA": { bg: "bg-yellow-100", text: "text-yellow-700" },
    "LP Chat": { bg: "bg-purple-100", text: "text-purple-700" },
    "Blank": { bg: "bg-gray-100", text: "text-gray-700" },
  }
  return mapping[status] || { bg: "bg-gray-100", text: "text-gray-700" }
}

// =============================================================================
// COLLAPSIBLE SECTION COMPONENT
// =============================================================================
const CollapsibleSection = ({ id, title, defaultExpanded = false, children, badge = null }) => {
  const [expanded, setExpanded] = useState(defaultExpanded)

  return (
    <div className="border border-slate-200 rounded-lg mb-3 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className={`w-full px-4 py-3 flex items-center justify-between bg-gradient-to-r from-slate-50 to-white hover:bg-slate-100 transition-colors ${expanded ? 'border-b border-slate-200' : ''}`}
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-slate-700">{title}</span>
          {badge && <span className="text-xs text-slate-400">{badge}</span>}
        </div>
        <svg
          className={`w-5 h-5 text-slate-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {expanded && <div className="p-4">{children}</div>}
    </div>
  )
}

// =============================================================================
// DRILL-DOWN MODAL COMPONENT
// =============================================================================
const DrillDownModal = ({ isOpen, onClose, stepData, users, loading, pagination, onPageChange, onSessionClick }) => {
  if (!isOpen) return null

  // Always show chat column since we now have L/A/S indicators for all touchpoints
  const statusColors = {
  "Signed up": "bg-blue-100 text-blue-700",
  "Completed GSA": "bg-green-100 text-green-700",
  "Started GSA": "bg-yellow-100 text-yellow-700",
  "LP Chat": "bg-purple-100 text-purple-700",
  "Blank": "bg-gray-100 text-gray-700",
};

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold text-slate-800">
              Users at: <span className="text-teal-600">{stepData?.name}</span>
            </h2>     
            <p className="text-sm text-slate-500 mt-0.5">
              {stepData?.count?.toLocaleString()} users
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 text-3xl leading-none"
          >
            &times;
          </button>
        </div>

        {/* Table */}
        <div className="p-6 overflow-y-auto flex-1 custom-scrollbar">
          {loading ? (
            <div className="text-center py-10 text-slate-500">Loading users...</div>
          ) : (
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                    Site
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                    Date / Time
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                    Location
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase" title="L=Landing, A=Post-Plan, S=Post-Session">
                    Chat
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                    Time on Step
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                    Device
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {users.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-10 text-slate-500">
                      No users found at this step.
                    </td>
                  </tr>
                ) : (
                  users.map((user, index) => (
                    <tr 
                    key={user.session_id || index} 
                    className="hover:bg-slate-50 cursor-pointer"
                    onClick={() => onSessionClick(user.session_id)}
                    >
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {(() => {
                          const site = user.device_type ? user.device_type.split('|')[1]?.trim() : null;
                          return site ? (
                            <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                              site.toLowerCase().includes('startfreept.com')
                                ? 'bg-teal-100 text-teal-700'
                                : 'bg-purple-100 text-purple-700'
                            }`}>
                              {site.toLowerCase().includes('startfreept.com') ? 'StartFreePT' : 'Virdio'}
                            </span>
                          ) : (
                            <span className="text-slate-400">—</span>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600">
                        {formatDateTime(user.session_start_time)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600">
                        {user.city && user.country
                          ? `${user.city}, ${user.country}`
                          : <span className="italic text-slate-400">Unknown</span>}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {stepData.name === 'Landed on StartFreePT' ? (
                          user.status && user.status !== 'Blank' ? (
                            <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${statusColors[user.status]}`}>
                              {user.status}
                            </span>
                          ) : (
                            <span className="text-slate-300">—</span>
                          )
                        ) : (
                          user.exit_reason ? (
                            <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-700">
                              {formatExitReason(user.exit_reason)}
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-700">
                              Completed
                            </span>
                          )
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {/* L=Landing, A=Post-Plan (Assessment), S=Post-Session */}
                        {user.chat_touchpoints ? (
                          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-purple-100 text-purple-700" title="L=Landing, A=Post-Plan, S=Post-Session">
                            {user.chat_touchpoints}
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600">
                        {formatDuration(user.time_on_step_sec)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600">
                        {user.device_type
                          ? user.device_type.split('|')[0]?.trim().charAt(0).toUpperCase() + user.device_type.split('|')[0]?.trim().slice(1)
                          : "Unknown"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination Footer */}
        <div className="p-4 border-t bg-slate-50 flex justify-between items-center">
          <button
            onClick={() => onPageChange(pagination.page - 1)}
            disabled={pagination.page <= 1}
            className="text-sm text-slate-500 hover:text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            ← Previous {pagination.limit}
          </button>
          <span className="text-sm text-slate-500">
            Showing {((pagination.page - 1) * pagination.limit) + 1}-
            {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
          </span>
          <button
            onClick={() => onPageChange(pagination.page + 1)}
            disabled={pagination.page * pagination.limit >= pagination.total}
            className="text-sm text-slate-500 hover:text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next {pagination.limit} →
          </button>
        </div>
      </div>

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar { width: 8px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #94a3b8; border-radius: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #64748b; }
      `}</style>
    </div>
  )
}

// =============================================================================
// SESSION DETAILS MODAL COMPONENT (Enhanced with Collapsible Sections)
// =============================================================================
const SessionDetailModal = ({ isOpen, onClose, sessionDetails, loading }) => {
  if (!isOpen) return null;

  // The API now returns a structured object (not an array)
  const data = sessionDetails;

  // Helper to determine status
  const getStatus = () => {
    if (!data) return { text: '', badge: '' }
    if (data.signed_up || data.final_conversion_type === 'signup') return { text: 'Signed Up', badge: 'bg-green-100 text-green-700' }
    if (data.recovery_score_viewed || data.viewed_plan) return { text: 'Completed GSA', badge: 'bg-blue-100 text-blue-700' }
    if (data.started_assessment) return { text: 'Started GSA', badge: 'bg-yellow-100 text-yellow-700' }
    if (data.chats && Object.keys(data.chats).length > 0) return { text: 'LP Chat', badge: 'bg-purple-100 text-purple-700' }
    return { text: '', badge: '' }
  }

  const status = getStatus()

  // Calculate total journey duration from screen_flow
  const screenFlow = data?.screen_flow || []
  const totalDuration = screenFlow.reduce((sum, s) => sum + (s.duration_sec || 0), 0)

  // Format primary goals
  const formatGoals = (goals) => {
    if (!goals) return '—'
    if (Array.isArray(goals)) return goals.map(g => g.text || g).join(', ')
    return goals
  }

  // Render ROM bar chart
  const renderROMChart = (romData) => {
    if (!romData || !Array.isArray(romData) || romData.length === 0) return null
    return (
      <div className="mt-3">
        <p className="text-xs text-slate-500 mb-2">ROM by Rep ({romData.length} reps)</p>
        <div className="flex gap-1 items-end h-8">
          {romData.map((r, i) => (
            <div
              key={i}
              className="flex-1 bg-gradient-to-t from-teal-600 to-teal-400 rounded-sm transition-all"
              style={{ height: `${r}%` }}
              title={`Rep ${i + 1}: ${r}%`}
            />
          ))}
        </div>
        <div className="flex justify-between text-xs text-slate-400 mt-1">
          <span>Rep 1</span>
          <span>Rep {romData.length}</span>
        </div>
      </div>
    )
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[55]"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-xl shadow-lg w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex justify-between items-center bg-gradient-to-r from-teal-50 to-white">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Session Details</h2>
            <div className="flex items-center gap-2 mt-0.5">
              <p className="text-xs text-slate-500 font-mono">{data?.session_id}</p>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  navigator.clipboard.writeText(data?.session_id || '')
                }}
                className="text-xs text-slate-400 hover:text-teal-600 transition-colors"
                title="Copy Session ID"
              >
                📋
              </button>
            </div>
          </div>
          <button onClick={onClose} className="text-2xl text-gray-500 hover:text-gray-700">&times;</button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto flex-1 custom-scrollbar">
          {loading ? (
            <div className="text-center text-gray-500 py-10">Loading session details...</div>
          ) : !data ? (
            <div className="text-center text-gray-500 py-10">No details found.</div>
          ) : (
            <div className="space-y-0">
              {/* SECTION 1: Overview */}
              <CollapsibleSection id="overview" title="📋 Overview" defaultExpanded={true}>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500 uppercase font-medium">Device</p>
                    <p className="text-sm font-semibold text-slate-800 mt-1 capitalize">{data.device_type || '—'}</p>
                    {data.device_info && (
                      <p className="text-xs text-slate-400 mt-0.5 truncate" title={data.device_info}>
                        {data.device_info.substring(0, 35)}...
                      </p>
                    )}
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500 uppercase font-medium">Location</p>
                    <p className="text-sm font-semibold text-slate-800 mt-1">
                      {[data.city, data.country].filter(Boolean).join(', ') || '—'}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500 uppercase font-medium">Session Started</p>
                    <p className="text-sm font-semibold text-slate-800 mt-1">
                      {formatDateTime(data.session_start_time)}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500 uppercase font-medium">Status</p>
                    {status.text ? (
                      <span className={`inline-block mt-1 px-2 py-0.5 text-xs font-semibold rounded-full ${status.badge}`}>
                        {status.text}
                      </span>
                    ) : (
                      <p className="text-sm text-slate-400 mt-1">—</p>
                    )}
                  </div>
                </div>
              </CollapsibleSection>

              {/* SECTION 2: Journey Timeline */}
              <CollapsibleSection
                id="journey"
                title="🗺️ Journey Timeline"
                defaultExpanded={true}
                badge={screenFlow.length > 0 ? `${screenFlow.length} screens, ${formatDuration(totalDuration)} total` : null}
              >
                {screenFlow.length === 0 ? (
                  <p className="text-sm text-slate-500">No journey data available.</p>
                ) : (
                  <div className="space-y-0">
                    {screenFlow.map((step, i) => {
                      const isCompleted = step.completed
                      const isFailed = step.exit_reason && ['camera_permission_denied', 'ar_init_failed', 'abandoned', 'timeout'].includes(step.exit_reason)
                      return (
                        <div
                          key={i}
                          className={`flex items-center py-2 border-l-2 ml-2 pl-4 relative ${
                            isFailed ? 'border-red-300' : isCompleted ? 'border-teal-300' : 'border-slate-200'
                          }`}
                        >
                          <div
                            className={`absolute -left-[5px] w-2 h-2 rounded-full ${
                              isFailed ? 'bg-red-500' : isCompleted ? 'bg-teal-500' : 'bg-slate-300'
                            }`}
                          />
                          <div className="flex-1 flex items-center justify-between">
                            <div>
                              <span className="text-sm font-medium text-slate-700">
                                {SCREEN_LABELS[step.screen] || step.screen}
                              </span>
                              {isFailed && (
                                <span className="ml-2 text-xs text-red-500">
                                  ({formatExitReason(step.exit_reason)})
                                </span>
                              )}
                            </div>
                            <div className="text-right">
                              <span className="text-sm text-slate-600">{formatDuration(step.duration_sec)}</span>
                              <span className="text-xs text-slate-400 ml-2">
                                {step.entered_at ? new Date(step.entered_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : ''}
                              </span>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </CollapsibleSection>

              {/* SECTION 3: Clinical Profile */}
              {(data.clinical_profile?.cpg_classification || data.clinical_profile?.pre_test_nprs) && (
                <CollapsibleSection id="clinical" title="🩺 Clinical Profile" defaultExpanded={false}>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Pain Zones</p>
                      <p className="text-sm font-medium text-slate-800 mt-1">
                        {data.clinical_profile?.selected_pain_zones?.join(', ') || '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase">CPG Classification</p>
                      <p className="text-sm font-medium text-slate-800 mt-1">
                        {data.clinical_profile?.cpg_classification || '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Pain Subtype</p>
                      <p className="text-sm font-medium text-slate-800 mt-1">
                        {data.clinical_profile?.probable_subtype?.replace(/_/g, ' ') || '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Pre-Test NPRS</p>
                      <p className="text-sm font-medium text-slate-800 mt-1">
                        {data.clinical_profile?.pre_test_nprs ? `${data.clinical_profile.pre_test_nprs}/10` : '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Symptom Duration</p>
                      <p className="text-sm font-medium text-slate-800 mt-1">
                        {data.clinical_profile?.symptom_duration || '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Primary Goal</p>
                      <p className="text-sm font-medium text-slate-800 mt-1">
                        {formatGoals(data.clinical_profile?.primary_goal)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Floor Capability</p>
                      <p className="text-sm font-medium text-slate-800 mt-1 capitalize">
                        {data.clinical_profile?.floor_capability || '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Balance</p>
                      <p className="text-sm font-medium text-slate-800 mt-1 capitalize">
                        {data.clinical_profile?.balance || '—'}
                      </p>
                    </div>
                  </div>
                </CollapsibleSection>
              )}

              {/* SECTION 4: Movement Tests */}
              {(data.movement_tests?.test1?.name || data.movement_tests?.test2?.name) && (
                <CollapsibleSection id="tests" title="🏃 Movement Tests" defaultExpanded={false}>
                  <div className="space-y-4">
                    {[data.movement_tests?.test1, data.movement_tests?.test2].map((test, idx) => {
                      if (!test?.name) return null
                      const responseColors = {
                        IMPROVED: 'text-green-600 bg-green-50',
                        SAME: 'text-amber-600 bg-amber-50',
                        WORSENED: 'text-red-600 bg-red-50'
                      }
                      return (
                        <div key={idx} className="border border-slate-200 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-semibold text-slate-700">Test {idx + 1}: {test.name}</h4>
                            <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                              test.completed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}>
                              {test.completed ? 'Completed' : 'Incomplete'}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div>
                              <p className="text-xs text-slate-500">Symptom Response</p>
                              <p className={`text-sm font-medium mt-1 px-2 py-0.5 rounded inline-block ${
                                responseColors[test.response] || 'text-slate-600 bg-slate-50'
                              }`}>
                                {test.response || '—'}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">RPE</p>
                              <p className="text-sm font-medium text-slate-800 mt-1">
                                {test.rpe !== null && test.rpe !== undefined ? `${test.rpe}/10` : '—'}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Video Watched</p>
                              <p className="text-sm font-medium text-slate-800 mt-1">
                                {test.video_watched_fully ? 'Yes' : 'No'}
                              </p>
                            </div>
                            {test.exit_reason && (
                              <div>
                                <p className="text-xs text-slate-500">Exit Reason</p>
                                <p className="text-sm font-medium text-red-600 mt-1">
                                  {formatExitReason(test.exit_reason)}
                                </p>
                              </div>
                            )}
                          </div>
                          {renderROMChart(test.rom_data)}
                        </div>
                      )
                    })}
                  </div>
                </CollapsibleSection>
              )}

              {/* SECTION 5: Demographics */}
              {(data.demographics?.age || data.demographics?.weight_lbs || data.demographics?.activity_level) && (
                <CollapsibleSection id="demographics" title="👤 Demographics" defaultExpanded={false}>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Age</p>
                      <p className="text-sm font-medium text-slate-800 mt-1">{data.demographics?.age || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Weight</p>
                      <p className="text-sm font-medium text-slate-800 mt-1">
                        {data.demographics?.weight_lbs ? `${data.demographics.weight_lbs} lbs` : '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Activity Level</p>
                      <p className="text-sm font-medium text-slate-800 mt-1">
                        {data.demographics?.activity_level?.replace(/_/g, ' ') || '—'}
                      </p>
                    </div>
                  </div>
                </CollapsibleSection>
              )}

              {/* SECTION 6: Recovery Plan */}
              {(data.recovery_plan?.final_recovery_score || data.recovery_plan?.plan_rationale_speech) && (
                <CollapsibleSection id="recovery" title="📊 Recovery Plan" defaultExpanded={false}>
                  <div className="space-y-4">
                    {data.recovery_plan?.final_recovery_score && (
                      <div className="flex items-center gap-4">
                        <div className="text-center">
                          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center">
                            <span className="text-xl font-bold text-white">{data.recovery_plan.final_recovery_score}</span>
                          </div>
                          <p className="text-xs text-slate-500 mt-1">Recovery Score</p>
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-slate-600">
                            This score represents the baseline assessment of movement capability and pain sensitivity.
                          </p>
                        </div>
                      </div>
                    )}
                    {data.recovery_plan?.plan_rationale_speech && (
                      <div className="bg-slate-50 rounded-lg p-4">
                        <p className="text-xs text-slate-500 uppercase mb-2">Myra's Plan Rationale</p>
                        <p className="text-sm text-slate-700 italic">"{data.recovery_plan.plan_rationale_speech}"</p>
                      </div>
                    )}
                  </div>
                </CollapsibleSection>
              )}

              {/* SECTION 7: Myra Conversations */}
              <CollapsibleSection id="chats" title="💬 Myra Conversations" defaultExpanded={true}>
                {data.chats && Object.keys(data.chats).length > 0 ? (
                  ['landing', 'post_plan', 'post_session'].filter(tp => data.chats[tp]?.length > 0).map(tp => {
                    const msgs = data.chats[tp]
                    const questionCount = msgs.filter(m => m.role === 'user').length
                    return (
                      <div key={tp} className="mb-4">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-2 h-2 rounded-full bg-purple-500" />
                          <span className="text-xs font-semibold text-purple-700 uppercase">{TOUCHPOINT_LABELS[tp]}</span>
                          <span className="text-xs text-slate-400">{questionCount} question{questionCount !== 1 ? 's' : ''}</span>
                        </div>
                        <div className="space-y-2 pl-4 border-l-2 border-purple-100">
                          {msgs.map((msg, idx) => (
                            <div
                              key={idx}
                              className={`rounded-lg p-3 ${
                                msg.role === 'user'
                                  ? 'bg-purple-50 border border-purple-100'
                                  : 'bg-slate-50 border border-slate-100'
                              }`}
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`text-xs font-semibold ${msg.role === 'user' ? 'text-purple-700' : 'text-slate-600'}`}>
                                  {msg.role === 'user' ? '👤 User' : '🤖 Myra'}
                                </span>
                                {msg.input_method && msg.role === 'user' && (
                                  <span className="px-1.5 py-0.5 text-xs rounded bg-white text-slate-500">{msg.input_method}</span>
                                )}
                              </div>
                              <p className="text-sm text-slate-700">{msg.content}</p>
                              {msg.intent && msg.role === 'assistant' && (
                                <p className="text-xs text-slate-400 mt-1">Intent: {msg.intent}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )
                  })
                ) : (
                  <div className="text-center py-6 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500">No Myra conversations for this session.</p>
                  </div>
                )}
              </CollapsibleSection>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t flex justify-end">
          <button onClick={onClose} className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm hover:bg-teal-700 transition-colors">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};


// =============================================================================
// APP TAB - USER DETAIL MODAL COMPONENT
// =============================================================================
const AppUserDetailModal = ({ isOpen, onClose, user, gsaData, sessions, loading, onSessionClick }) => {
  if (!isOpen) return null

  // Status badge helper
  const getStatusBadge = (status) => {
    const mapping = {
      'F': { label: 'Free', className: 'bg-green-100 text-green-700' },
      'P': { label: 'Paid', className: 'bg-blue-100 text-blue-700' },
      'E': { label: 'Expired', className: 'bg-red-100 text-red-700' }
    }
    return mapping[status] || { label: status, className: 'bg-gray-100 text-gray-700' }
  }

  const statusBadge = getStatusBadge(user?.status)

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[55]"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex justify-between items-center bg-gradient-to-r from-blue-50 to-white">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">User Details</h2>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-sm font-medium text-blue-700">{user?.name || 'Unknown'}</span>
              <span className="text-xs text-slate-400">•</span>
              <span className="text-xs text-slate-500">{user?.email}</span>
              <span className="text-xs text-slate-400">•</span>
              <span className="text-xs text-slate-500">ID: {user?.user_id}</span>
              <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${statusBadge.className}`}>
                {statusBadge.label}
              </span>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-3xl leading-none">&times;</button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto flex-1 custom-scrollbar">
          {loading ? (
            <div className="text-center text-gray-500 py-10">Loading user details...</div>
          ) : (
            <div className="space-y-0">
              {/* SECTION 1: GSA Overview */}
              <CollapsibleSection id="gsa-overview" title="📋 GSA Overview" defaultExpanded={true}>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500 uppercase font-medium">GSA Date</p>
                    <p className="text-sm font-semibold text-slate-800 mt-1">{formatDate(gsaData?.gsa_date)}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500 uppercase font-medium">Device</p>
                    <p className="text-sm font-semibold text-slate-800 mt-1 capitalize">{gsaData?.device_type || '—'}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500 uppercase font-medium">Location</p>
                    <p className="text-sm font-semibold text-slate-800 mt-1">
                      {[gsaData?.city, gsaData?.state].filter(Boolean).join(', ') || '—'}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500 uppercase font-medium">Recovery Score</p>
                    {gsaData?.recovery_score ? (
                      <div className="flex items-center gap-2 mt-1">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center">
                          <span className="text-sm font-bold text-white">{gsaData.recovery_score}</span>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-slate-400 mt-1">—</p>
                    )}
                  </div>
                </div>
              </CollapsibleSection>

              {/* SECTION 2: Clinical Profile */}
              <CollapsibleSection id="clinical-profile" title="🩺 Clinical Profile" defaultExpanded={true}>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Pain Zones</p>
                    <p className="text-sm font-medium text-slate-800 mt-1">
                      {gsaData?.pain_zones?.join(', ') || '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">CPG Classification</p>
                    <p className="text-sm font-medium text-slate-800 mt-1">
                      {gsaData?.cpg_classification || '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Pain Subtype</p>
                    <p className="text-sm font-medium text-slate-800 mt-1">
                      {gsaData?.pain_subtype?.replace(/_/g, ' ') || '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Pre-Test NPRS</p>
                    <p className="text-sm font-medium text-slate-800 mt-1">
                      {gsaData?.pre_test_nprs ? `${gsaData.pre_test_nprs}/10` : '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Symptom Duration</p>
                    <p className="text-sm font-medium text-slate-800 mt-1">
                      {gsaData?.symptom_duration || '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Primary Goal</p>
                    <p className="text-sm font-medium text-slate-800 mt-1">
                      {gsaData?.primary_goal || '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Floor Capability</p>
                    <p className="text-sm font-medium text-slate-800 mt-1 capitalize">
                      {gsaData?.floor_capability || '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Balance</p>
                    <p className="text-sm font-medium text-slate-800 mt-1 capitalize">
                      {gsaData?.balance || '—'}
                    </p>
                  </div>
                </div>
              </CollapsibleSection>

              {/* SECTION 3: GSA Movement Tests */}
              {(gsaData?.test1 || gsaData?.test2) && (
                <CollapsibleSection id="gsa-tests" title="🏃 GSA Movement Tests" defaultExpanded={false}>
                  <div className="space-y-4">
                    {[gsaData?.test1, gsaData?.test2].map((test, idx) => {
                      if (!test?.name) return null
                      const responseColors = {
                        IMPROVED: 'text-green-600 bg-green-50',
                        SAME: 'text-amber-600 bg-amber-50',
                        WORSENED: 'text-red-600 bg-red-50'
                      }
                      return (
                        <div key={idx} className="border border-slate-200 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-semibold text-slate-700">Test {idx + 1}: {test.name}</h4>
                            <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                              test.completed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}>
                              {test.completed ? 'Completed' : 'Incomplete'}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div>
                              <p className="text-xs text-slate-500">Symptom Response</p>
                              <p className={`text-sm font-medium mt-1 px-2 py-0.5 rounded inline-block ${
                                responseColors[test.symptom_response] || 'text-slate-600 bg-slate-50'
                              }`}>
                                {test.symptom_response || '—'}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">RPE</p>
                              <p className="text-sm font-medium text-slate-800 mt-1">
                                {test.rpe !== null && test.rpe !== undefined ? `${test.rpe}/10` : '—'}
                              </p>
                            </div>
                          </div>
                          {test.rom_data && test.rom_data.length > 0 && (
                            <div className="mt-3">
                              <p className="text-xs text-slate-500 mb-2">ROM by Rep ({test.rom_data.length} reps)</p>
                              <div className="flex gap-1 items-end h-8">
                                {test.rom_data.map((r, i) => (
                                  <div
                                    key={i}
                                    className="flex-1 bg-gradient-to-t from-teal-600 to-teal-400 rounded-sm"
                                    style={{ height: `${r}%` }}
                                    title={`Rep ${i + 1}: ${r}%`}
                                  />
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </CollapsibleSection>
              )}

              {/* SECTION 4: Recovery Plan Rationale */}
              {gsaData?.plan_rationale_speech && (
                <CollapsibleSection id="plan-rationale" title="💬 Recovery Plan Rationale" defaultExpanded={false}>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-sm text-slate-700 italic">"{gsaData.plan_rationale_speech}"</p>
                  </div>
                </CollapsibleSection>
              )}

              {/* SECTION 5: Exercise Sessions */}
              <CollapsibleSection 
                id="exercise-sessions" 
                title={`🏋️ Exercise Sessions (${sessions?.length || 0} total)`} 
                defaultExpanded={true}
              >
                {sessions && sessions.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200 text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Planned Date</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Actual Date</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Sets Target</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Sets Actual</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Progressions</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Regressions</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Questions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-slate-200">
                        {sessions.map((session, idx) => (
                          <tr 
                            key={session.session_id || idx} 
                            className="hover:bg-slate-50 cursor-pointer"
                            onClick={() => onSessionClick(session)}
                          >
                            <td className="px-3 py-2 text-slate-600">{formatDate(session.planned_date)}</td>
                            <td className="px-3 py-2 text-slate-600">
                              {session.actual_date ? formatDate(session.actual_date) : <span className="text-amber-500">Pending</span>}
                            </td>
                            <td className="px-3 py-2 text-slate-600">{session.sets_target || '—'}</td>
                            <td className={`px-3 py-2 ${session.sets_actual < session.sets_target ? 'text-amber-600' : 'text-teal-600'}`}>
                              {session.sets_actual ?? '—'}
                            </td>
                            <td className="px-3 py-2">
                              {session.progressions > 0 ? (
                                <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-700">+{session.progressions}</span>
                              ) : (
                                <span className="text-slate-400">—</span>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              {session.regressions > 0 ? (
                                <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-700">-{session.regressions}</span>
                              ) : (
                                <span className="text-slate-400">—</span>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              {session.questions > 0 ? (
                                <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-purple-100 text-purple-700">{session.questions}</span>
                              ) : (
                                <span className="text-slate-400">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p className="text-xs text-slate-400 mt-3 text-center">Click a session row to view exercise details and post-session chat</p>
                  </div>
                ) : (
                  <div className="text-center py-6 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500">No exercise sessions found.</p>
                  </div>
                )}
              </CollapsibleSection>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t flex justify-end">
          <button onClick={onClose} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors">
            Close
          </button>
        </div>
      </div>

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar { width: 8px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #94a3b8; border-radius: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #64748b; }
      `}</style>
    </div>
  )
}

// =============================================================================
// APP TAB - SESSION DETAIL MODAL COMPONENT
// =============================================================================
const AppSessionDetailModal = ({ isOpen, onClose, session, sets, chat, loading }) => {
  if (!isOpen) return null

  const isCompleted = session?.actual_date != null

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-[60]"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex justify-between items-center bg-gradient-to-r from-amber-50 to-white">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Session Details</h2>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-xs text-slate-500">{formatDate(session?.actual_date || session?.planned_date)}</span>
              <span className="text-xs text-slate-400">•</span>
              <span className={`text-xs font-medium ${isCompleted ? 'text-teal-600' : 'text-amber-600'}`}>
                {isCompleted ? 'Completed' : 'Pending'}
              </span>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-3xl leading-none">&times;</button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto flex-1 custom-scrollbar">
          {loading ? (
            <div className="text-center text-gray-500 py-10">Loading session details...</div>
          ) : (
            <div className="space-y-0">
              {/* SECTION 1: Exercise Sets */}
              <CollapsibleSection 
                id="exercise-sets" 
                title={`🏋️ Exercise Sets (${sets?.length || 0} exercises)`} 
                defaultExpanded={true}
              >
                {sets && sets.length > 0 ? (
                  <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Exercise</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Reps (Target/Actual)</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Hold (Target/Actual)</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Progression</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Regression</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-200">
                      {sets.map((set, idx) => (
                        <tr key={idx} className="hover:bg-slate-50">
                          <td className="px-3 py-2 font-medium text-slate-700">{set.exercise_name}</td>
                          <td className="px-3 py-2 text-slate-600">
                            {set.reps_target} / <span className={set.reps_actual < set.reps_target ? 'text-amber-600' : 'text-teal-600'}>{set.reps_actual}</span>
                          </td>
                          <td className="px-3 py-2 text-slate-600">
                            {set.hold_target}s / <span className={set.hold_actual < set.hold_target ? 'text-amber-600' : 'text-teal-600'}>{set.hold_actual}s</span>
                          </td>
                          <td className="px-3 py-2">
                            {set.progression ? (
                              <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-700">{set.progression}</span>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            {set.regression ? (
                              <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-700">{set.regression}</span>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-center py-6 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500">Session not yet completed.</p>
                  </div>
                )}
              </CollapsibleSection>

              {/* SECTION 2: Post-Session Review */}
              <CollapsibleSection 
                id="post-session-chat" 
                title={`💬 Post-Session Review (${chat?.filter(m => m.role === 'user').length || 0} questions)`} 
                defaultExpanded={true}
              >
                {chat && chat.length > 0 ? (
                  <div className="space-y-3">
                    {chat.map((msg, idx) => (
                      <div
                        key={idx}
                        className={`rounded-lg p-3 ${
                          msg.role === 'user'
                            ? 'bg-purple-50 border border-purple-100'
                            : 'bg-slate-50 border border-slate-100'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs font-semibold ${msg.role === 'user' ? 'text-purple-700' : 'text-slate-600'}`}>
                            {msg.role === 'user' ? '👤 User' : '🤖 Myra'}
                          </span>
                          {msg.input_method && msg.role === 'user' && (
                            <span className="px-1.5 py-0.5 text-xs rounded bg-white text-slate-500">{msg.input_method}</span>
                          )}
                        </div>
                        <p className="text-sm text-slate-700">{msg.content}</p>
                        {msg.intent && msg.role === 'assistant' && (
                          <p className="text-xs text-slate-400 mt-1">Intent: {msg.intent}</p>
                        )}
                        <p className="text-xs text-slate-400 mt-1">{formatDateTime(msg.created_at)}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500">No post-session conversation.</p>
                  </div>
                )}
              </CollapsibleSection>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-slate-50 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm hover:bg-amber-700 transition-colors">
            Close
          </button>
        </div>
      </div>

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar { width: 8px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #94a3b8; border-radius: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #64748b; }
      `}</style>
    </div>
  )
}

// =============================================================================
// MAIN ANALYTICS PAGE COMPONENT
// =============================================================================
const AnalyticsPage = (props) => {
  const { refreshKey } = props
  
  // =============================================================================
  // TAB STATE
  // =============================================================================
  const [activeTab, setActiveTab] = useState("gsa") // "gsa" or "app"
  
  // =============================================================================
  // GSA TAB STATE (existing)
  // =============================================================================
  const [dateRange, setDateRange] = useState("today")
  const [siteFilter, setSiteFilter] = useState('all')
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone; // get user timezone
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [lastUpdated, setLastUpdated] = useState("")
  const [funnelView, setFunnelView] = useState("simple") // "simple" or "detailed"

  // Drill-down modal state
  const [modalOpen, setModalOpen] = useState(false)
  const [modalStepData, setModalStepData] = useState(null)
  const [modalUsers, setModalUsers] = useState([])
  const [modalLoading, setModalLoading] = useState(false)
  const [modalPagination, setModalPagination] = useState({ page: 1, limit: 50, total: 0 })

  // Session Details modal state
  const [sessionDetailModalOpen, setSessionDetailModalOpen] = useState(false);
  const [selectedSessionDetails, setSelectedSessionDetails] = useState(null);
  const [sessionDetailsLoading, setSessionDetailsLoading] = useState(false);

  // chat states
  const [chatModalOpen, setChatModalOpen] = useState(false)
  const [chatTouchpoint, setChatTouchpoint] = useState(null) // 'landing' or 'post_plan'
  const [chatSessions, setChatSessions] = useState([])
  const [chatLoading, setChatLoading] = useState(false)
  const [chatDetailsLoading, setChatDetailsLoading] = useState(false)
  const [chatPagination, setChatPagination] = useState({ page: 1, limit: 50, total: 0 })
  // Chat Detail Modal State (for individual conversation view)
  const [chatDetailModalOpen, setChatDetailModalOpen] = useState(false)
  const [selectedChatSession, setSelectedChatSession] = useState(null)
  const [chatMessages, setChatMessages] = useState([])

  // =============================================================================
  // APP TAB STATE (new - independent from GSA tab)
  // =============================================================================
  const [appDateRange, setAppDateRange] = useState("today")
  const [appKpis, setAppKpis] = useState(null)
  const [appUsers, setAppUsers] = useState([])
  const [appUsersPagination, setAppUsersPagination] = useState({ total: 0, limit: 50, offset: 0, page: 1 })
  const [appUsersSearch, setAppUsersSearch] = useState('')
  const [appUsersStatusFilter, setAppUsersStatusFilter] = useState('all')
  const [appLoading, setAppLoading] = useState(false)
  const [appLastUpdated, setAppLastUpdated] = useState("")
  const [appUsersSearchInput, setAppUsersSearchInput] = useState('')

  // App Tab Modal State
  const [appUserDetailModalOpen, setAppUserDetailModalOpen] = useState(false)
  const [selectedAppUser, setSelectedAppUser] = useState(null)
  const [selectedAppUserGSA, setSelectedAppUserGSA] = useState(null)
  const [selectedAppUserSessions, setSelectedAppUserSessions] = useState([])
  const [appUserDetailLoading, setAppUserDetailLoading] = useState(false)

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      if (appUsersSearchInput !== appUsersSearch) {
        setAppUsersSearch(appUsersSearchInput)
        fetchAppUsers(1, appUsersStatusFilter, appUsersSearchInput)
      }
    }, 400)
    return () => clearTimeout(timer)
  }, [appUsersSearchInput])

  // App Tab Session Detail Modal State
  const [appSessionDetailModalOpen, setAppSessionDetailModalOpen] = useState(false)
  const [selectedAppSession, setSelectedAppSession] = useState(null)
  const [selectedAppSessionSets, setSelectedAppSessionSets] = useState([])
  const [selectedAppSessionChat, setSelectedAppSessionChat] = useState([])
  const [appSessionDetailLoading, setAppSessionDetailLoading] = useState(false)

  // clicked on refresh -> recalling the api
    useEffect(() => {
    if (refreshKey > 0) {
      if (activeTab === 'gsa') {
        fetchDashboardData()
      } else {
        fetchAppData()
      }
    }
  }, [refreshKey, activeTab])


  // get start and end time to fetch data 

  const {startDate, endDate} = getTimeZoneDateRange(timezone, dateRange); 
  const appDateRangeResult = getTimeZoneDateRange(timezone, appDateRange);
  const appStartDate = appDateRangeResult.startDate;
  const appEndDate = appDateRangeResult.endDate;

  // =============================================================================
  // GSA TAB API CALLS (existing)
  // =============================================================================

  // Fetch session Details for landed on startFreePt 
  const fetchSessionDetails = async (sessionId) => {
  
    setSessionDetailsLoading(true);

  try {
    const token = JSON.parse(localStorage.getItem("userData") || "{}")?.token;

    if (token) {
          axios.defaults.headers.common["Authorization"] = token
        }

        const response = await axios.get(
          `${window.ajaxLink}cloud_dashboard/analytics/drilldown/session_details?sessionId=${sessionId}&range=${dateRange}&start_date=${startDate}&end_date=${endDate}`
        )

        const result = response.data?.responseData
        console.log("sessionDetails", result)

    setSelectedSessionDetails(result|| null);
   
  } catch (err) {
    console.error("Failed to fetch session details:", err);
    setSelectedSessionDetails(null);
  } finally {
    setSessionDetailsLoading(false);
  }
};

  // helper funtion for session details 
  const handleSessionClick = (sessionId) => {
  setSessionDetailModalOpen(true);
  fetchSessionDetails(sessionId);
};


  // fetching chats
  // Fetch chat sessions list for a specific touchpoint
  // API now returns pre-aggregated sessions with question_count and message_count
  const fetchChatSessions = useCallback(async (touchpoint, page = 1) => {
      setChatLoading(true)

      try {
        const token = JSON.parse(localStorage.getItem("userData") || "{}")?.token
        if (token) {
          axios.defaults.headers.common["Authorization"] = token
        }

        const response = await axios.get(
          `${window.ajaxLink}cloud_dashboard/analytics/prechats?range=${dateRange}&page=${page}&limit=50&touchpoint=${touchpoint}&start_date=${startDate}&end_date=${endDate}`
        )

        const result = response.data?.responseData
        
        // API now returns pre-aggregated sessions (not individual messages)
        // Each item already has session_id, question_count, message_count, etc.
        const sessions = result?.chats?.map(session => ({
          session_id: session.session_id,
          user_id: session.user_id,
          conversation_id: session.conversation_id,
          touchpoint: session.touchpoint,
          created_at: session.created_at,
          message_count: session.message_count || 0,
          question_count: session.question_count || 0,
          location: [session.city, session.country].filter(Boolean).join(', ') || 'Unknown',
          device: session.device_type || 'Unknown',
          signed_up: session.signed_up,
          started_gsa: session.started_assessment,
          completed_gsa: session.completed_assessment
        })) || []

        setChatSessions(sessions)
        setChatPagination({
          page: result?.page || 1,
          limit: result?.limit || 50,
          total: result?.total || 0,
        })
      } catch (err) {
        console.error("Failed to fetch chat sessions:", err)
        setChatSessions([])
      } finally {
        setChatLoading(false)
      }
  }, [dateRange, startDate, endDate])

  // handlers for chat:
  // Open chat modal for specific touchpoint
const openChatModal = (touchpoint) => {
  setChatTouchpoint(touchpoint)
  setChatModalOpen(true)
  fetchChatSessions(touchpoint, 1)
}

// Handle chat modal pagination
const handleChatPageChange = (newPage) => {
  if (newPage < 1) return
  fetchChatSessions(chatTouchpoint, newPage)
}

// Open detail view for a specific chat session
// For post_session, we use conversation_id; for others, we use session_id
const openChatDetail = (session) => {
  setSelectedChatSession(session)
  setChatDetailModalOpen(true)
  
  // Fetch full conversation details
  // For post_session, pass conversation_id; for others, pass session_id
  if (chatTouchpoint === 'post_session') {
    fetchChatDetail(null, session.conversation_id)
  } else {
    fetchChatDetail(session.session_id, null)
  }
}

// Fetch full conversation for a session or conversation
const fetchChatDetail = async (sessionId, conversationId = null) => {
  setChatDetailsLoading(true)
  try {
    const token = JSON.parse(localStorage.getItem("userData") || "{}")?.token
    if (token) {
      axios.defaults.headers.common["Authorization"] = token
    }

    // Build query params - use conversation_id for post_session, session_id for others
    let url = `${window.ajaxLink}cloud_dashboard/analytics/prechats?range=${dateRange}&touchpoint=${chatTouchpoint}&start_date=${startDate}&end_date=${endDate}`
    if (conversationId) {
      url += `&conversation_id=${conversationId}`
    } else if (sessionId) {
      url += `&session_id=${sessionId}`
    }

    const response = await axios.get(url)

    const result = response.data?.responseData
    setChatMessages(result?.chats || [])
  } catch (err) {
    console.error("Failed to fetch chat details:", err)
    setChatMessages([])
  } finally {
    setChatDetailsLoading(false)
  }
}

// chat modal
// =============================================================================
// MYRA CHAT SESSIONS MODAL COMPONENT
// =============================================================================
const ChatSessionsModal = ({ isOpen, onClose, touchpoint, sessions, loading, pagination, onPageChange, onSessionClick }) => {
  if (!isOpen) return null

  const touchpointLabels = {
    landing: 'Landing Page',
    post_plan: 'Post-Assessment',
    post_session: 'In-App (Post-Session)'
  }
  const touchpointLabel = touchpointLabels[touchpoint] || touchpoint
  const showUserId = touchpoint === 'post_session' // Show user_id for in-app chats

  // Status badge helper
  const getSessionStatus = (session) => {
    if (session.signed_up) return { text: 'Signed Up', badge: 'bg-blue-100 text-blue-700' }
    if (session.completed_gsa) return { text: 'Completed GSA', badge: 'bg-green-100 text-green-700' }
    if (session.started_gsa) return { text: 'Started GSA', badge: 'bg-yellow-100 text-yellow-700' }
    return { text: '', badge: '' }
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex justify-between items-center bg-gradient-to-r from-purple-50 to-white">
          <div>
            <h2 className="text-lg font-semibold text-purple-800">
              Myra Conversations: <span className="text-purple-600">{touchpointLabel}</span>
            </h2>
            <p className="text-sm text-slate-500 mt-0.5">
              {pagination.total?.toLocaleString()} users asked questions
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 text-3xl leading-none"
          >
            &times;
          </button>
        </div>

        {/* Sessions List - 2 column grid with compact cards */}
        <div className="p-4 overflow-y-auto flex-1 custom-scrollbar">
          {loading ? (
            <div className="text-center py-10 text-slate-500">Loading chat sessions...</div>
          ) : sessions.length === 0 ? (
            <div className="col-span-2 text-center py-8 text-slate-500">No conversations found.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {sessions.map((session, index) => {
                const status = getSessionStatus(session)
                const questionCount = session.question_count || session.message_count || 0
                return (
                  <div
                    key={session.conversation_id || session.session_id || index}
                    onClick={() => onSessionClick(session)}
                    className="border border-purple-100 rounded-lg p-3 hover:bg-purple-50 cursor-pointer transition-all hover:shadow-md flex justify-between items-center"
                  >
                    <div className="space-y-1">
                      {/* Line 1: Date/Time + Q count badge */}
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-slate-700">
                          {formatDateTime(session.created_at)}
                        </span>
                        <span className="px-1.5 py-0.5 text-xs font-semibold rounded-full bg-purple-100 text-purple-700">
                          {questionCount} Q
                        </span>
                      </div>
                      {/* Line 2: User ID (for post_session) OR Location • Device */}
                      {showUserId && session.user_id ? (
                        <div className="text-sm text-blue-600 font-medium">
                          User: {session.user_id}
                        </div>
                      ) : (
                        <div className="text-sm text-slate-600">
                          {session.location || 'Unknown'} • {session.device || 'Unknown'}
                        </div>
                      )}
                      {/* Line 3: Session ID or Conversation ID (truncated) + copy button */}
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono text-slate-400">
                          {(session.conversation_id || session.session_id)?.substring(0, 8)}...
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            navigator.clipboard.writeText(session.conversation_id || session.session_id || '')
                            e.target.textContent = '✓'
                            setTimeout(() => { e.target.textContent = '📋' }, 1500)
                          }}
                          className="text-xs text-slate-400 hover:text-purple-600"
                          title="Copy ID"
                        >
                          📋
                        </button>
                      </div>
                    </div>
                    {/* Status badge on right */}
                    <div className="flex items-center">
                      {status.text && (
                        <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${status.badge}`}>
                          {status.text}
                        </span>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Pagination Footer */}
        <div className="p-4 border-t bg-slate-50 flex justify-between items-center">
          <button
            onClick={() => onPageChange(pagination.page - 1)}
            disabled={pagination.page <= 1}
            className="text-sm text-slate-500 hover:text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            ← Previous
          </button>
          <span className="text-sm text-slate-500">
            Page {pagination.page} of {Math.ceil(pagination.total / pagination.limit)}
          </span>
          <button
            onClick={() => onPageChange(pagination.page + 1)}
            disabled={pagination.page * pagination.limit >= pagination.total}
            className="text-sm text-slate-500 hover:text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  )
}
// =============================================================================
// CHAT DETAIL MODAL COMPONENT (shows full conversation)
// =============================================================================
const ChatDetailModal = ({ isOpen, onClose, session, loading, messages }) => {
  if (!isOpen) return null

  // Sort messages chronologically (oldest first) for natural reading flow
  const sortedMessages = [...messages].sort((a, b) => {
    const dateCompare = new Date(a.created_at) - new Date(b.created_at);
    if (dateCompare !== 0) return dateCompare;
    return (a.turn_number || 0) - (b.turn_number || 0);
  });

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-[60]"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex justify-between items-center bg-gradient-to-r from-purple-50 to-white">
          <div>
            <h2 className="text-lg font-semibold text-purple-800">Conversation Details</h2>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-xs text-slate-500">{session?.location || 'Unknown Location'}</span>
              <span className="text-xs text-slate-400">•</span>
              <span className="text-xs text-slate-500">{session?.device || 'Unknown Device'}</span>
              {session?.user_id && (
                <>
                  <span className="text-xs text-slate-400">•</span>
                  <span className="text-xs text-blue-600 font-medium">User: {session.user_id}</span>
                </>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-3xl leading-none">&times;</button>
        </div>

        {/* Chat Messages - Chronological order, oldest at top */}
        <div className="p-6 overflow-y-auto flex-1 custom-scrollbar space-y-4">
          {loading ? (
            <div className="text-center py-10 text-slate-500">Loading messages...</div>
          ) : sortedMessages.length === 0 ? (
            <div className="text-center py-10 text-slate-500">No messages in this conversation.</div>
          ) : (
            sortedMessages.map((msg, index) => (
              <div
                key={msg.id || index}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[80%] ${msg.role === 'user' ? 'bg-purple-50 border border-purple-100' : 'bg-slate-50 border border-slate-100'} rounded-lg p-4`}>
                  {/* Message Header */}
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-xs font-semibold ${msg.role === 'user' ? 'text-purple-700' : 'text-slate-600'}`}>
                      {msg.role === 'user' ? '👤 User' : '🤖 Myra'}
                    </span>
                    {msg.input_method && msg.role === 'user' && (
                      <span className="px-1.5 py-0.5 text-xs rounded bg-white text-slate-500">
                        {msg.input_method}
                      </span>
                    )}
                  </div>

                  {/* Message Content */}
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{msg.content}</p>

                  {/* Intent Info - only for assistant */}
                  {msg.intent && msg.role === 'assistant' && (
                    <div className="mt-2 pt-2 border-t border-slate-200">
                      <span className="text-xs text-slate-500">
                        Intent: <span className="font-medium text-slate-700">{msg.intent}</span>
                        {msg.intent_confidence && (
                          <span className="ml-2 text-slate-400">
                            ({(msg.intent_confidence * 100).toFixed(0)}%)
                          </span>
                        )}
                      </span>
                    </div>
                  )}

                  {/* Timestamp */}
                  <p className="text-xs text-slate-400 mt-2">
                    {new Date(msg.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-slate-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

  // =============================================================================
  // DATA FETCHING
  // =============================================================================
  const fetchDashboardData = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const token = JSON.parse(localStorage.getItem("userData") || "{}")?.token
      if (token) {
        axios.defaults.headers.common["Authorization"] = token
      }

      const response = await axios.get(
        `${window.ajaxLink}cloud_dashboard/analytics?range=${dateRange}&start_date=${startDate}&end_date=${endDate}&site=${siteFilter}`
      )
      setData(response.data?.responseData)
      setLastUpdated(new Date().toLocaleString())
    } catch (err) {
      setError(err?.message || "An unknown error occurred")
    } finally {
      setLoading(false)
    }
  }, [dateRange, startDate, endDate, siteFilter])

  const fetchDrillDownUsers = useCallback(async (stepId, page = 1) => {
    setModalLoading(true)

    try {
      const token = JSON.parse(localStorage.getItem("userData") || "{}")?.token
      if (token) {
        axios.defaults.headers.common["Authorization"] = token
      }

      const response = await axios.get(
        `${window.ajaxLink}cloud_dashboard/analytics/drilldown?step=${stepId}&range=${dateRange}&page=${page}&limit=50&start_date=${startDate}&end_date=${endDate}&site=${siteFilter}`
      )

      const result = response.data?.responseData
      setModalUsers(result?.users || [])
      setModalPagination({
        page: result?.page || 1,
        limit: result?.limit || 50,
        total: result?.total || 0,
      })
    } catch (err) {
      console.error("Failed to fetch drill-down users:", err)
      setModalUsers([])
    } finally {
      setModalLoading(false)
    }
  }, [dateRange, startDate, endDate, siteFilter])

  useEffect(() => {
    fetchDashboardData()
  }, [fetchDashboardData])

  // =============================================================================
  // APP TAB DATA FETCHING (new)
  // =============================================================================
  const fetchAppData = useCallback(async () => {
    setAppLoading(true)

    try {
      const token = JSON.parse(localStorage.getItem("userData") || "{}")?.token
      if (token) {
        axios.defaults.headers.common["Authorization"] = token
      }

      // Fetch App KPIs
      const kpisResponse = await axios.get(
        `${window.ajaxLink}cloud_dashboard/analytics/app/kpis?startDate=${appStartDate}&endDate=${appEndDate}`
      )
      setAppKpis(kpisResponse.data?.responseData)

      // Fetch App Users
      await fetchAppUsers(1, appUsersStatusFilter, appUsersSearch)

      setAppLastUpdated(new Date().toLocaleString())
    } catch (err) {
      console.error("Failed to fetch app data:", err)
    } finally {
      setAppLoading(false)
    }
  }, [appDateRange, appStartDate, appEndDate])

  const fetchAppUsers = useCallback(async (page = 1, status = 'all', search = '') => {
    try {
      const token = JSON.parse(localStorage.getItem("userData") || "{}")?.token
      if (token) {
        axios.defaults.headers.common["Authorization"] = token
      }

      const offset = (page - 1) * 50
      let url = `${window.ajaxLink}cloud_dashboard/analytics/app/users?startDate=${appStartDate}&endDate=${appEndDate}&limit=50&offset=${offset}`
      
      if (status && status !== 'all') {
        url += `&status=${status}`
      }
      if (search) {
        url += `&search=${encodeURIComponent(search)}`
      }

      const response = await axios.get(url)
      const result = response.data?.responseData

      const total = result?.pagination?.total ?? 0
      const limit = result?.pagination?.limit ?? 50

      setAppUsers(result?.users || [])
      setAppUsersPagination({
        total,
        limit,
        offset: result?.pagination?.offset || 0,
        page
      })
    } catch (err) {
      console.error("Failed to fetch app users:", err)
      setAppUsers([])
    }
  }, [appStartDate, appEndDate])

  const fetchAppUserDetails = useCallback(async (userId) => {
    setAppUserDetailLoading(true)

    try {
      const token = JSON.parse(localStorage.getItem("userData") || "{}")?.token
      if (token) {
        axios.defaults.headers.common["Authorization"] = token
      }

      // Fetch GSA data
      const gsaResponse = await axios.get(
        `${window.ajaxLink}cloud_dashboard/analytics/app/users/${userId}/gsa`
      )
      setSelectedAppUserGSA(gsaResponse.data?.responseData)

      // Fetch sessions
      const sessionsResponse = await axios.get(
        `${window.ajaxLink}cloud_dashboard/analytics/app/users/${userId}/sessions`
      )
      setSelectedAppUserSessions(sessionsResponse.data?.responseData?.sessions || [])

    } catch (err) {
      console.error("Failed to fetch app user details:", err)
      setSelectedAppUserGSA(null)
      setSelectedAppUserSessions([])
    } finally {
      setAppUserDetailLoading(false)
    }
  }, [])

  const fetchAppSessionDetails = useCallback(async (sessionId) => {
    setAppSessionDetailLoading(true)

    try {
      const token = JSON.parse(localStorage.getItem("userData") || "{}")?.token
      if (token) {
        axios.defaults.headers.common["Authorization"] = token
      }

      // Fetch sets
      const setsResponse = await axios.get(
        `${window.ajaxLink}cloud_dashboard/analytics/app/sessions/${sessionId}/sets`
      )
      setSelectedAppSessionSets(setsResponse.data?.responseData?.sets || [])

      // Fetch chat
      const chatResponse = await axios.get(
        `${window.ajaxLink}cloud_dashboard/analytics/app/sessions/${sessionId}/chat`
      )
      setSelectedAppSessionChat(chatResponse.data?.responseData?.messages || [])

    } catch (err) {
      console.error("Failed to fetch app session details:", err)
      setSelectedAppSessionSets([])
      setSelectedAppSessionChat([])
    } finally {
      setAppSessionDetailLoading(false)
    }
  }, [])

  // Fetch App data when tab changes or date range changes
  useEffect(() => {
    if (activeTab === 'app') {
      fetchAppData()
    }
  }, [activeTab, appDateRange])

  // =============================================================================
  // APP TAB HANDLERS
  // =============================================================================
  const handleAppUserRowClick = (user) => {
    setSelectedAppUser(user)
    setAppUserDetailModalOpen(true)
    fetchAppUserDetails(user.user_id)
  }

  const handleAppSessionRowClick = (session) => {
    setSelectedAppSession(session)
    setAppSessionDetailModalOpen(true)
    fetchAppSessionDetails(session.session_id)
  }

  const handleAppUsersPageChange = (newPage) => {
    if (newPage < 1) return
    fetchAppUsers(newPage, appUsersStatusFilter, appUsersSearch)
  }

  const handleAppUsersSearch = (searchTerm) => {
    setAppUsersSearch(searchTerm)
    fetchAppUsers(1, appUsersStatusFilter, searchTerm)
  }

  const handleAppUsersStatusFilter = (status) => {
    setAppUsersStatusFilter(status)
    fetchAppUsers(1, status, appUsersSearch)
  }

  const handleAppKpiClick = (filterType) => {
    // Set filter based on KPI clicked
    if (filterType === 'active' || filterType === 'engaged') {
      // For now, these would need backend support
      // We'll just refresh the users table
      fetchAppUsers(1, 'all', appUsersSearch)
    } else if (['F', 'P', 'E'].includes(filterType)) {
      handleAppUsersStatusFilter(filterType)
    }
  }

  // =============================================================================
  // DRILL-DOWN HANDLERS
  // =============================================================================
  const openDrillDown = (step) => {
    setModalStepData(step)
    setModalOpen(true)
    fetchDrillDownUsers(step.id, 1)
  }

  const handlePageChange = (newPage) => {
    if (newPage < 1) return
    fetchDrillDownUsers(modalStepData.id, newPage)
  }

  // =============================================================================
  // FUNNEL DATA
  // =============================================================================
  const getFunnelSteps = () => {
    if (!data) return []

    if (funnelView === "simple") {
      return [
        { id: "landed", name: "Landed on StartFreePT", count: data.kpis?.totalSessions || 0, section: "landing", myraTouch: null },
        { id: "started", name: "Started GSA", count: data.funnelSteps?.find(s => s.id === "started")?.count || 0, section: "gsa", myraTouch: null },
        { id: "viewed_plan", name: "Viewed Recovery Plan", count: data.funnelSteps?.find(s => s.id === "viewed_plan")?.count || 0, section: "plan", myraTouch: null },
        { id: "signed_up", name: "Signed Up", count: data.funnelSteps?.find(s => s.id === "signed_up")?.count || 0, section: "signup", myraTouch: null, highlight: true },
      ]
    }

    // Detailed view - all steps from API
    return data.funnelStepsDetailed || []
  }

  // =============================================================================
  // CHART DATA GENERATORS
  // =============================================================================
  const getCpgChartData = () => {
    if (!data?.charts?.cpg) return null
    const cpgTotal = data.charts.cpg.reduce((sum, item) => sum + item.count, 0)
    if (cpgTotal === 0) return null

    return {
      labels: data.charts.cpg.map((item) => item.cpg_classification.replace("LBP with ", "")),
      datasets: [{
        data: data.charts.cpg.map((item) => item.count),
        backgroundColor: ["#14b8a6", "#0891b2", "#f59e0b", "#6366f1"],
      }],
    }
  }

  const getCpgChartOptions = () => {
    const cpgTotal = data?.charts?.cpg?.reduce((sum, item) => sum + item.count, 0) || 0
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: "bottom", labels: { boxWidth: 12, font: { size: 11 } } },
        datalabels: {
          formatter: (value) => ((value * 100) / cpgTotal).toFixed(0) + "%",
          color: "#fff",
          font: { weight: "bold" },
        },
      },
    }
  }

  const getSymptomChartData = () => {
    if (!data?.charts?.symptom) return null
    const { improved, tolerable, worsened } = data.charts.symptom
    if (!improved && !tolerable && !worsened) return null

    return {
      labels: ["Improved", "Same", "Worsened"],
      datasets: [{
        label: "Users",
        data: [improved || 0, tolerable || 0, worsened || 0],
        backgroundColor: ["#10b981", "#f59e0b", "#ef4444"],
      }],
    }
  }

  const getSymptomChartOptions = () => ({
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y",
    plugins: { legend: { display: false }, datalabels: { display: false } },
  })

  const getDeviceChartData = () => {
    if (!data?.charts?.devices) return null
    const { mobile, desktop, tablet } = data.charts.devices
    if (!mobile && !desktop && !tablet) return null

    return {
      labels: ["Mobile", "Desktop", "Tablet"],
      datasets: [{
        data: [mobile || 0, desktop || 0, tablet || 0],
        backgroundColor: ["#10b981", "#3b82f6", "#f97316"],
      }],
    }
  }

  const getDeviceChartOptions = () => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "bottom", labels: { boxWidth: 12, font: { size: 11 } } },
      datalabels: { display: false },
    },
  })

  const getPainZonesChartData = () => {
    if (!data?.charts?.painZones) return null
    const zones = data.charts.painZones
    if (!zones || zones.length === 0) return null

    return {
      labels: zones.map((z) => z.zone),
      datasets: [{
        label: "Users",
        data: zones.map((z) => z.count),
        backgroundColor: "#14b8a6",
      }],
    }
  }

  const getPainZonesChartOptions = () => ({
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y",
    plugins: { legend: { display: false }, datalabels: { display: false } },
  })

  // =============================================================================
  // RENDER: FUNNEL
  // =============================================================================
  const renderFunnel = () => {
    const steps = getFunnelSteps()
    if (!steps || steps.length === 0) return null

    const maxCount = Math.max(...steps.map((s) => s.count || 0), 1)

    return (
      <div className="space-y-2">
        {steps.map((step, index) => {
          const prevCount = index > 0 ? steps[index - 1].count : step.count
          const dropOff = prevCount > 0 ? ((prevCount - step.count) / prevCount * 100).toFixed(1) : 0
          const widthPct = (step.count / maxCount) * 100

          return (
            <div
              key={step.id}
              className={`funnel-step relative p-3 rounded-lg border cursor-pointer transition-all ${
                step.highlight
                  ? "bg-gradient-to-r from-teal-50 to-white border-teal-300"
                  : "bg-white border-slate-200 hover:border-teal-300"
              }`}
              onClick={() => openDrillDown(step)}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-slate-700">{step.name}</span>
                <div className="flex items-center gap-3">
                  <span className={`text-sm font-bold ${step.highlight ? "text-teal-600" : "text-slate-800"}`}>
                    {step.count?.toLocaleString()}
                  </span>
                  {index > 0 && dropOff > 0 && (
                    <span className="text-xs text-red-500">-{dropOff}%</span>
                  )}
                </div>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all ${
                    step.highlight ? "bg-teal-500" : "bg-teal-400"
                  }`}
                  style={{ width: `${widthPct}%` }}
                />
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  // =============================================================================
  // RENDER: SCREEN TIMING
  // =============================================================================
  const renderScreenTiming = () => {
    if (!data?.screenTiming) return null

    const maxDuration = Math.max(...data.screenTiming.map((s) => s.avg_duration_sec || 0), 1)

    return (
      <div className="space-y-3">
        {data.screenTiming.map((screen) => {
          const widthPct = (screen.avg_duration_sec / maxDuration) * 100
          const isHighlight = ["test1_ar", "test1_demo", "test2_ar", "personalize"].includes(screen.screen)

          return (
            <div key={screen.screen}>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-600">{SCREEN_LABELS[screen.screen] || screen.screen}</span>
                <span className={`font-medium ${isHighlight ? "text-amber-600" : "text-slate-800"}`}>
                  {formatDuration(screen.avg_duration_sec)}
                </span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${isHighlight ? "bg-amber-500" : "bg-teal-500"}`}
                  style={{ width: `${widthPct}%` }}
                />
              </div>
            </div>
          )
        })}
        <p className="text-xs text-slate-400 mt-2 text-center">Highlighted = AR/video screens</p>
      </div>
    )
  }

  // =============================================================================
  // RENDER: COMPLETION LIKELIHOOD
  // =============================================================================
  const renderCompletionLikelihood = () => {
    if (!data?.completionLikelihood) return null

    return (
      <div className="space-y-3">
        {data.completionLikelihood.map((item) => (
          <div key={item.step}>
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm text-slate-600">{item.label}</span>
              <span className={`text-sm font-bold ${item.isKeyMilestone ? "text-teal-600" : "text-slate-700"}`}>
                {item.likelihood}%
              </span>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-2">
              <div
                className="h-2 rounded-full bg-teal-500"
                style={{ width: `${item.likelihood}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    )
  }

  // =============================================================================
  // RENDER: MOVEMENT TEST ANALYSIS
  // =============================================================================
  const renderMovementTestAnalysis = () => {
    if (!data?.movementTests) return null

    const renderTestCard = (test, testNum) => {
      if (!test) return null
      // const completionRate = test.started > 0 ? ((test.completed / test.started) * 100).toFixed(1) : 0
      const completionRate = test.started > 0 ? Math.min((test.completed / test.started) * 100, 100).toFixed(1) : 0;

      return (
        <div className="border border-slate-200 rounded-lg p-4">
          <h3 className="font-medium text-slate-700 mb-3">
            Movement Test {testNum}
            {testNum === 2 && <span className="text-xs text-slate-400 ml-2">(conditional)</span>}
          </h3>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">Started</span>
              <span className="font-semibold text-slate-800">{test.started?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">Completed</span>
              <span className="font-semibold text-teal-600">{test.completed?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">Dropped</span>
              <span className="font-semibold text-red-500">{test.dropped?.toLocaleString()}</span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-2 mt-2">
              <div className="bg-teal-500 h-2 rounded-full" style={{ width: `${completionRate}%` }} />
            </div>
            <p className="text-xs text-slate-500 text-right">{completionRate}% completion rate</p>
          </div>
          {test.dropReasons && test.dropReasons.length > 0 && (
            <div className="mt-4 pt-3 border-t border-slate-100">
              <p className="text-xs text-slate-500 mb-2">Drop-off Reasons</p>
              <div className="space-y-1">
                {test.dropReasons.map((reason, i) => (
                  <div key={i} className="flex justify-between items-center text-sm">
                    <span className="text-slate-600">{formatExitReason(reason.reason)}</span>
                    <span
                      className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                        reason.reason === "user_skipped"
                          ? "bg-amber-100 text-amber-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {reason.count}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {data.movementTests.test1 && renderTestCard(data.movementTests.test1, 1)}
        {data.movementTests.test2 && renderTestCard(data.movementTests.test2, 2)}
      </div>
    )
  }

  // =============================================================================
  // RENDER: MYRA IMPACT
  // =============================================================================
  const renderMyraImpact = () => {
    if (!data?.myraImpact) return null

    const { landing, postPlan } = data.myraImpact

    return (
      <div className="space-y-4">
        <div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-purple-700">With Myra (Landing)</span>
            <span className="font-bold text-purple-800">{landing?.withMyra || 0}%</span>
          </div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-slate-500">Without Myra</span>
            <span className="font-medium text-slate-600">{landing?.withoutMyra || 0}%</span>
          </div>
          <p className="text-xs text-purple-600 mt-1">
            +{((landing?.withMyra || 0) - (landing?.withoutMyra || 0)).toFixed(1)}% GSA start rate
          </p>
        </div>
        <div className="border-t border-purple-100 pt-4">
          <div className="flex justify-between text-sm mb-1">
            <span className="text-purple-700">With Myra (Post-Plan)</span>
            <span className="font-bold text-purple-800">{postPlan?.withMyra || 0}%</span>
          </div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-slate-500">Without Myra</span>
            <span className="font-medium text-slate-600">{postPlan?.withoutMyra || 0}%</span>
          </div>
          <p className="text-xs text-purple-600 mt-1">
            +{((postPlan?.withMyra || 0) - (postPlan?.withoutMyra || 0)).toFixed(1)}% signup rate
          </p>
        </div>
      </div>
    )
  }

  // =============================================================================
  // RENDER: APP TAB CONTENT
  // =============================================================================
  const renderAppTabContent = () => {
    return (
      <>
        {/* Filters Section - App Tab */}
        <section className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <label
              htmlFor="date-range-app"
              className="text-sm font-medium text-slate-600 whitespace-nowrap"
            >
              Date Range
            </label>
            <select
              id="date-range-app"
              value={appDateRange}
              onChange={(e) => setAppDateRange(e.target.value)}
              className="rounded-lg border-slate-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 text-sm px-3 py-2 bg-white border"
            >
              <option value="today">Today</option>
              <option value="wtd">Week to Date</option>
              <option value="mtd">Month to Date</option>
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
            </select>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-green-500"></span>
              <span className="text-slate-600">Free</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-blue-500"></span>
              <span className="text-slate-600">Paid</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-red-500"></span>
              <span className="text-slate-600">Expired</span>
            </div>
          </div>
        </section>

        {appLoading ? (
          <div className="text-center py-12">
            <div className="text-slate-500">Loading app data...</div>
          </div>
        ) : (
          <>
            {/* App KPI Cards - Row 1: Conversion & Status */}
            <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
              <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                  Started GSA
                </p>
                <p className="mt-1 text-2xl font-bold text-slate-800">
                  {appKpis?.startedGSA?.toLocaleString() || 0}
                </p>
                <p className="text-xs text-slate-400 mt-1">Total assessments</p>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                  Signed Up
                </p>
                <p className="mt-1 text-2xl font-bold text-teal-600">
                  {appKpis?.signedUp?.toLocaleString() || 0}
                </p>
                <p className="text-xs text-teal-600 mt-1">
                  {appKpis?.conversionRate || 0}% conversion
                </p>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                  Conversion Rate
                </p>
                <p className="mt-1 text-2xl font-bold text-teal-600">
                  {appKpis?.conversionRate || 0}%
                </p>
                <p className="text-xs text-slate-400 mt-1">GSA → Signup</p>
              </div>
              <button
                onClick={() => handleAppKpiClick("active")}
                className="bg-gradient-to-br from-green-50 to-white rounded-xl p-4 shadow-sm border border-green-200 hover:shadow-md transition-shadow text-left"
              >
                <p className="text-xs font-medium text-green-600 uppercase tracking-wide">
                  Active Users
                </p>
                <p className="mt-1 text-2xl font-bold text-green-700">
                  {appKpis?.activeUsers?.toLocaleString() || 0}
                </p>
                <p className="text-xs text-green-500 mt-1">
                  Within 7-day trial
                </p>
              </button>
              <button
                onClick={() => handleAppKpiClick("F")}
                className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-4 shadow-sm border border-blue-200 hover:shadow-md transition-shadow text-left"
              >
                <p className="text-xs font-medium text-blue-600 uppercase tracking-wide">
                  Free Users
                </p>
                <p className="mt-1 text-2xl font-bold text-blue-700">
                  {appKpis?.freeUsers?.toLocaleString() || 0}
                </p>
                <p className="text-xs text-blue-500 mt-1">Active free tier</p>
              </button>
              <button
                onClick={() => handleAppKpiClick("P")}
                className="bg-gradient-to-br from-indigo-50 to-white rounded-xl p-4 shadow-sm border border-indigo-200 hover:shadow-md transition-shadow text-left"
              >
                <p className="text-xs font-medium text-indigo-600 uppercase tracking-wide">
                  Paid Users
                </p>
                <p className="mt-1 text-2xl font-bold text-indigo-700">
                  {appKpis?.paidUsers?.toLocaleString() || 0}
                </p>
                <p className="text-xs text-indigo-500 mt-1">
                  Converted to paid
                </p>
              </button>
            </section>

            {/* App KPI Cards - Row 2: Engagement & Sessions */}
            <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
              <button
                onClick={() => handleAppKpiClick("engaged")}
                className="bg-gradient-to-br from-amber-50 to-white rounded-xl p-4 shadow-sm border border-amber-200 hover:shadow-md transition-shadow text-left"
              >
                <p className="text-xs font-medium text-amber-600 uppercase tracking-wide">
                  Engaged Users
                </p>
                <p className="mt-1 text-2xl font-bold text-amber-700">
                  {appKpis?.engagedUsers?.toLocaleString() || 0}
                </p>
                <p className="text-xs text-amber-500 mt-1">
                  Session in last 7 days
                </p>
              </button>
              <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                  Total Sessions
                </p>
                <p className="mt-1 text-2xl font-bold text-slate-800">
                  {appKpis?.totalSessions?.toLocaleString() || 0}
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  All completed sessions
                </p>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                  Avg Sessions/User
                </p>
                <p className="mt-1 text-2xl font-bold text-slate-800">
                  {appKpis?.avgSessionsPerUser || 0}
                </p>
                <p className="text-xs text-slate-400 mt-1">Per active user</p>
              </div>
              <button
                onClick={() => openChatModal("post_session")}
                className="bg-gradient-to-br from-purple-50 to-white rounded-xl p-4 shadow-sm border border-purple-200 hover:shadow-md transition-shadow text-left"
              >
                <p className="text-xs font-medium text-purple-600 uppercase tracking-wide">
                  Chats - Post Session
                </p>
                <p className="mt-1 text-2xl font-bold text-purple-700">
                  {appKpis?.postSessionChats?.toLocaleString() || 0}
                </p>
                <p className="text-xs text-purple-500 mt-1">
                  In-app conversations
                </p>
              </button>
              <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 opacity-60">
                <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">
                  Recovery Analytics
                </p>
                <p className="mt-1 text-2xl font-bold text-slate-400">—</p>
                <p className="text-xs text-slate-400 mt-1">Coming soon</p>
              </div>
            </section>

            <section className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden min-w-0">
              {/* Header — stays full width, never scrolls */}
              <div className="p-4 border-b border-slate-200 flex flex-col md:flex-row md:justify-between md:items-center gap-2">
                <div className="mb-3">
                  <h2 className="text-lg font-semibold text-slate-800">
                    Users
                  </h2>
                  <p className="text-xs text-slate-400 mt-1">
                    Click a row to view user details
                  </p>
                </div>
                <div className="flex w sm:items-center sm:justify-end gap-2">
                  <input
                    type="text"
                    placeholder="Search by name or email..."
                    value={appUsersSearchInput}
                    onChange={(e) => setAppUsersSearchInput(e.target.value)}
                    className="flex-1 min-w-0 text-sm border border-slate-300 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-teal-500"
                    style={{ minWidth: '150px' }}
                  />
                  <select
                    value={appUsersStatusFilter}
                    onChange={(e) => handleAppUsersStatusFilter(e.target.value)}
                    className="text-sm border border-slate-300 rounded-lg px-3 py-2 bg-white cursor-pointer"
                  >
                    <option value="all">All Status</option>
                    <option value="F">Free</option>
                    <option value="P">Paid</option>
                    <option value="E">Expired</option>
                  </select>
                </div>
              </div>
              <div
                style={{
                  overflowX: "auto",
                  WebkitOverflowScrolling: "touch",
                  scrollbarWidth: "thin",
                  scrollbarColor: "#94a3b8 #f1f5f9",
                }}
              >
                <div style={{ width: "100%", minWidth: "800px" }}>
                  {/* Column Headers */}
                  <div
                   className="grid items-center px-4 py-2 bg-slate-50 border-b border-slate-200 grid-cols-[90px_90px_60px_1fr_1fr_60px_80px_80px]"
                  >
                    <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider whitespace-nowrap pr-2">
                      Last Active
                    </div>
                    <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider  pr-2">
                      Sign Up
                    </div>
                    <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider truncate pr-2">
                      Status
                    </div>
                    <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider truncate pr-2 border-r border-slate-200">
                      Name
                    </div>
                    <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider truncate px-2">
                      Email
                    </div>
                    <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider truncate pr-2">
                      City
                    </div>
                    <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider  pr-2">
                      Sessions
                    </div>
                    <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider ">
                      Chats
                    </div>
                  </div>

                  {/* Rows */}
                  <div>
                    {appUsers.length === 0 ? (
                      <div className="text-center py-10 text-slate-400 text-sm">
                        No users found.
                      </div>
                    ) : (
                      appUsers.map((user, idx) => {
                        const statusBadge = {
                          F: { label: "F", bg: "#dcfce7", color: "#15803d" },
                          P: { label: "P", bg: "#dbeafe", color: "#1d4ed8" },
                          E: { label: "E", bg: "#fee2e2", color: "#b91c1c" },
                        }[user.status] || {
                          label: user.status || "?",
                          bg: "#f1f5f9",
                          color: "#475569",
                        };

                        return (
                          <div
                            key={user.user_id || idx}
                            onClick={() => handleAppUserRowClick(user)}
                   className="grid items-center px-4 py-2 bg-slate-50 border-b border-slate-200 grid-cols-[90px_90px_60px_1fr_1fr_60px_80px_80px]"
                          >
                            <div className="text-xs text-slate-500 pr-2 whitespace-nowrap">
                              {user.last_active ? (
                                formatDate(user.last_active)
                              ) : (
                                <span className="text-slate-300 italic">—</span>
                              )}
                            </div>
                            <div className="text-xs text-slate-500 pr-2 whitespace-nowrap">
                              {formatDate(user.signup_date)}
                            </div>
                            <div className="pr-2">
                              <span
                                className="inline-block px-1.5 py-0.5 rounded-full text-xs font-bold"
                                style={{
                                  background: statusBadge.bg,
                                  color: statusBadge.color,
                                }}
                              >
                                {statusBadge.label}
                              </span>
                            </div>
                            <div className="text-sm font-medium text-slate-700 truncate pr-2 border-r border-slate-200">
                              {user.name || "—"}
                            </div>
                            <div className="text-xs text-slate-500 truncate px-2">
                              {user.email}
                            </div>
                            <div className="text-xs text-slate-500 truncate pr-2">
                              {user.city || "—"}
                            </div>
                            <div className="text-sm text-slate-500 text-center pr-2">
                              {user.session_count || 0}
                            </div>
                            <div className="flex items-center gap-1">
                              {user.chat_count > 0 ? (
                                <span className="px-1.5 py-0.5 rounded-full text-xs font-bold bg-purple-100 text-purple-700">
                                  {user.chat_count}
                                </span>
                              ) : (
                                <span className="text-slate-300 text-xs">
                                  —
                                </span>
                              )}
                              {user.myra_session_chats > 0 && (
                                <span
                                  className="px-1 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-800"
                                  title={`${user.myra_session_chats} post-session chats`}
                                >
                                  S:{user.myra_session_chats}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>

              {/* Pagination — stays full width, never scrolls */}
              <div className="px-4 py-3.5 border-t border-slate-200 bg-slate-50 flex justify-between items-center">
                <button
                  onClick={() =>
                    handleAppUsersPageChange(appUsersPagination.page - 1)
                  }
                  disabled={appUsersPagination.page <= 1}
                  className="text-sm text-slate-500 hover:text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  ← Previous 50
                </button>
                <span className="text-sm text-slate-400">
                  {appUsersPagination.total === 0
                    ? "No results"
                    : `Showing ${
                        (appUsersPagination.page - 1) * 50 + 1
                      }–${Math.min(
                        appUsersPagination.page * 50,
                        appUsersPagination.total,
                      )} of ${appUsersPagination.total}`}
                </span>
                <button
                  onClick={() =>
                    handleAppUsersPageChange(appUsersPagination.page + 1)
                  }
                  disabled={
                    appUsersPagination.page * 50 >= appUsersPagination.total
                  }
                  className="text-sm text-slate-500 hover:text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next 50 →
                </button>
              </div>
            </section>
          </>
        )}
      </>
    );
  }

  // =============================================================================
  // RENDER: ERROR STATE
  // =============================================================================
  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200 text-center">
          <h2 className="text-xl font-bold text-red-600">Connection Error</h2>
          <p className="text-slate-600 mt-2">
            Could not load dashboard data. Please ensure the backend server is running and accessible.
          </p>
          <p className="text-sm text-slate-500 mt-4">
            <strong>Details:</strong> {error}
          </p>
          <button
            onClick={fetchDashboardData}
            className="mt-6 bg-teal-600 text-white font-semibold py-2 px-5 rounded-lg text-sm hover:bg-teal-700"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  // =============================================================================
  // RENDER: MAIN
  // =============================================================================
  return (
  <div className="space-y-6" style={{ maxWidth: "calc(100vw - 20px)" }}>
      {/* Tab Navigation */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="flex">
          <button
            onClick={() => setActiveTab('gsa')}
            className={`px-6 py-3 font-semibold text-sm border-b-3 transition-all ${
              activeTab === 'gsa'
                ? 'text-teal-600 border-b-2 border-teal-600 bg-teal-50'
                : 'text-slate-500 border-b-2 border-transparent hover:text-teal-600 hover:bg-teal-50'
            }`}
          >
            📊 GSA KPI's & Analysis
          </button>
          <button
            onClick={() => setActiveTab('app')}
            className={`px-6 py-3 font-semibold text-sm border-b-3 transition-all ${
              activeTab === 'app'
                ? 'text-teal-600 border-b-2 border-teal-600 bg-teal-50'
                : 'text-slate-500 border-b-2 border-transparent hover:text-teal-600 hover:bg-teal-50'
            }`}
          >
            📱 App KPI's & Analysis
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'gsa' ? (
        <>
          {/* GSA Tab - Filters Section */}
          <section className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3 flex-wrap">
              <label htmlFor="date-range" className="text-sm font-medium text-slate-600 whitespace-nowrap">
                Date Range
              </label>
              <select
                id="date-range"
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="rounded-lg border-slate-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 text-sm px-3 py-2 bg-white border"
              >
                <option value="today">Today</option>
                <option value="wtd">Week to Date</option>
                <option value="mtd">Month to Date</option>
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
                <option value="90d">Last 90 Days</option>
                <option value="all">All Time</option>
              </select>
              <label htmlFor="site-filter" className="text-sm font-medium text-slate-600 whitespace-nowrap">
                Site
              </label>
              <select
                id="site-filter"
                value={siteFilter}
                onChange={(e) => setSiteFilter(e.target.value)}
                className="rounded-lg border-slate-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 text-sm px-3 py-2 bg-white border"
              >
                <option value="all">All Sites</option>
                <option value="startfreept">StartFreePT</option>
                <option value="virdio">Virdio Health</option>
              </select>
            </div>
            {lastUpdated && (
              <div className="text-xs text-slate-500 sm:text-sm sm:ml-auto">
                <span className="font-medium text-slate-600">Last Updated:</span> {lastUpdated}
              </div>
            )}
          </section>

          {loading ? (
            <div className="text-center py-12">
              <div className="text-slate-500">Loading dashboard data...</div>
            </div>
          ) : (
            data && (
              <>
                {/* KPI Cards - Row 1: Core Metrics */}
                <section className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  <button 
                    onClick={() => openDrillDown({
                      id: "landed",
                      name: "Landed on StartFreePT",
                      count: data.kpis?.totalSessions || 0,
                      section: "landing",
                      myraTouch: "landing"
                    })}
                    className="bg-gradient-to-br from-teal-50 to-white rounded-xl p-4 shadow-sm border-2 border-teal-200 hover:border-teal-400 transition-all cursor-pointer text-left"
                  >
                    <p className="text-xs font-medium text-teal-600 uppercase tracking-wide">Landed</p>
                    <p className="mt-1 text-2xl font-bold text-slate-800">
                      {data.kpis?.totalSessions?.toLocaleString() || 0}
                    </p>
                    <p className="text-xs text-teal-500 mt-1">Total visitors • Click to view</p>
                  </button>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Started GSA</p>
                    <p className="mt-1 text-2xl font-bold text-slate-800">
                      {data.kpis?.startedGsa?.toLocaleString() || 0}
                    </p>
                    <p className="text-xs text-teal-600 mt-1">{data.kpis?.startRate || 0}% of landed</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Viewed Plan</p>
                    <p className="mt-1 text-2xl font-bold text-slate-800">
                      {data.kpis?.viewedPlan?.toLocaleString() || 0}
                    </p>
                    <p className="text-xs text-teal-600 mt-1">{data.kpis?.completionRate || 0}% of started</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Signed Up</p>
                    <p className="mt-1 text-2xl font-bold text-teal-600">
                      {data.kpis?.signedUp?.toLocaleString() || 0}
                    </p>
                    <p className="text-xs text-teal-600 mt-1">{data.kpis?.signUpRate || 0}% of viewed</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Overall Conversion</p>
                    <p className="mt-1 text-2xl font-bold text-teal-600">{data.kpis?.overallConversion || 0}%</p>
                    <p className="text-xs text-slate-400 mt-1">Land → Signup</p>
                  </div>
                </section>

                {/* KPI Cards - Row 2: Myra & Timing Metrics */}
                <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  {/* Myra Chat - Landing */}
                  <button 
                    onClick={() => openChatModal('landing')} 
                    className="bg-gradient-to-br from-purple-50 to-white rounded-xl p-4 shadow-sm border border-purple-100 hover:shadow-md transition-shadow cursor-pointer text-left"
                  >
                    <p className="text-xs font-medium text-purple-600 uppercase tracking-wide">Myra Chat (Landing)</p>
                    <p className="mt-1 text-2xl font-bold text-purple-700">
                      {data.kpis?.myraLandingCount?.toLocaleString() || 0}
                    </p>
                    <p className="text-xs text-purple-500 mt-1">{data.kpis?.myraLandingRate || 0}% engaged</p>
                  </button>

                  {/* Myra Chat - Post-Plan */}
                  <button 
                    onClick={() => openChatModal('post_plan')} 
                    className="bg-gradient-to-br from-purple-50 to-white rounded-xl p-4 shadow-sm border border-purple-100 hover:shadow-md transition-shadow cursor-pointer text-left"
                  >
                    <p className="text-xs font-medium text-purple-600 uppercase tracking-wide">Myra Chat (Post-Plan)</p>
                    <p className="mt-1 text-2xl font-bold text-purple-700">
                      {data.kpis?.myraPostPlanCount?.toLocaleString() || 0}
                    </p>
                    <p className="text-xs text-purple-500 mt-1">{data.kpis?.myraPostPlanRate || 0}% of viewers</p>
                  </button>

                  {/* Myra Chat - Post-Session (In-App) */}
                  <button 
                    onClick={() => openChatModal('post_session')} 
                    className="bg-gradient-to-br from-purple-50 to-white rounded-xl p-4 shadow-sm border border-purple-100 hover:shadow-md transition-shadow cursor-pointer text-left"
                  >
                    <p className="text-xs font-medium text-purple-600 uppercase tracking-wide">Myra Chat (In-App)</p>
                    <p className="mt-1 text-2xl font-bold text-purple-700">
                      {data.kpis?.myraPostSessionCount?.toLocaleString() || 0}
                    </p>
                    <p className="text-xs text-purple-500 mt-1">Post-session chats</p>
                  </button>

                  <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Avg Time on Landing</p>
                    <p className="mt-1 text-2xl font-bold text-slate-800">
                      {formatDuration(data.kpis?.avgTimeLanding)}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">Before GSA start</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Home Video Views</p>
                    <p className="mt-1 text-2xl font-bold text-teal-600">
                      {data.kpis?.homeVideoWatch?.toLocaleString() || 0}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">Users who watched video</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Avg GSA Duration</p>
                    <p className="mt-1 text-2xl font-bold text-slate-800">
                      {formatDuration(data.kpis?.avgGsaDuration)}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">Start to completion</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Test 1 Completion</p>
                    <p className="mt-1 text-2xl font-bold text-slate-800">{data.kpis?.test1CompletionRate || 0}%</p>
                    <p className="text-xs text-slate-400 mt-1">Of those who started</p>
                  </div>
                </section>

                {/* Main Content Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Left Column: Detailed Funnel (2/3 width) */}
                  <div className="lg:col-span-2 space-y-6">
                    {/* Detailed Journey Funnel */}
                    <section className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h2 className="text-lg font-semibold text-slate-800">User Journey Funnel</h2>
                          <p className="text-xs text-slate-500 mt-0.5">Click any step to see individual users</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setFunnelView("simple")}
                            className={`text-xs px-3 py-1.5 rounded-lg font-medium ${
                              funnelView === "simple"
                                ? "bg-teal-100 text-teal-700"
                                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                            }`}
                          >
                            Simple
                          </button>
                          <button
                            onClick={() => setFunnelView("detailed")}
                            className={`text-xs px-3 py-1.5 rounded-lg font-medium ${
                              funnelView === "detailed"
                                ? "bg-teal-100 text-teal-700"
                                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                            }`}
                          >
                            Detailed
                          </button>
                        </div>
                      </div>
                      {renderFunnel()}
                    </section>

                    {/* Movement Test Analysis */}
                    <section className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                      <h2 className="text-lg font-semibold text-slate-800 mb-4">Movement Test Analysis</h2>
                      {renderMovementTestAnalysis()}
                    </section>
                  </div>

                  {/* Right Column: Insights (1/3 width) */}
                  <div className="space-y-6">
                    {/* Screen Time Analysis */}
                    <section className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                      <h2 className="text-lg font-semibold text-slate-800 mb-4">Avg. Time Per Screen</h2>
                      {renderScreenTiming()}
                    </section>

                    {/* Point of No Return */}
                    <section className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                      <h2 className="text-lg font-semibold text-slate-800 mb-2">Completion Likelihood</h2>
                      <p className="text-xs text-slate-500 mb-4">
                        If user reaches this step, what's the chance they complete GSA?
                      </p>
                      {renderCompletionLikelihood()}
                    </section>

                    {/* Myra Impact Preview */}
                    <section className="bg-gradient-to-br from-purple-50 to-white rounded-xl p-6 shadow-sm border border-purple-100">
                      <h2 className="text-lg font-semibold text-purple-800 mb-2">Myra Impact</h2>
                      <p className="text-xs text-purple-600 mb-4">Conversion comparison</p>
                      {renderMyraImpact()}
                    </section>
                  </div>
                </div>

                {/* Charts Row */}
                <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                    <h3 className="text-lg font-semibold text-slate-800 mb-4">Clinical Profiles (CPG)</h3>
                    <div className="h-64">
                      {getCpgChartData() ? (
                        <Doughnut data={getCpgChartData()} options={getCpgChartOptions()} />
                      ) : (
                        <div className="flex items-center justify-center h-full text-slate-500 font-medium">
                          No Data
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                    <h3 className="text-lg font-semibold text-slate-800 mb-4">Symptom Response (Test 1)</h3>
                    <div className="h-64">
                      {getSymptomChartData() ? (
                        <Bar data={getSymptomChartData()} options={getSymptomChartOptions()} />
                      ) : (
                        <div className="flex items-center justify-center h-full text-slate-500 font-medium">
                          No Data
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                    <h3 className="text-lg font-semibold text-slate-800 mb-4">Device Distribution</h3>
                    <div className="h-64">
                      {getDeviceChartData() ? (
                        <Doughnut data={getDeviceChartData()} options={getDeviceChartOptions()} />
                      ) : (
                        <div className="flex items-center justify-center h-full text-slate-500 font-medium">
                          No Data
                        </div>
                      )}
                    </div>
                  </div>
                </section>

                {/* Pain Zones Chart */}
                <section className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                  <h3 className="text-lg font-semibold text-slate-800 mb-4">Top Problem Areas Selected</h3>
                  <div className="h-80">
                    {getPainZonesChartData() ? (
                      <Bar data={getPainZonesChartData()} options={getPainZonesChartOptions()} />
                    ) : (
                      <div className="flex items-center justify-center h-full text-slate-500 font-medium">
                        No Data
                      </div>
                    )}
                  </div>
                </section>
              </>
            )
          )}
        </>
      ) : (
        // App Tab Content
        renderAppTabContent()
      )}

      {/* Drill-Down Modal */}
      <DrillDownModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        stepData={modalStepData}
        users={modalUsers}
        loading={modalLoading}
        pagination={modalPagination}
        onPageChange={handlePageChange}
        onSessionClick = {handleSessionClick}
      />

      {/* Session-Details Modal */}
      <SessionDetailModal
        isOpen={sessionDetailModalOpen}
        onClose={() => setSessionDetailModalOpen(false)}
        sessionDetails={selectedSessionDetails}
        loading={sessionDetailsLoading}
      />

      {/* Chat Sessions Modal */}
      <ChatSessionsModal
        isOpen={chatModalOpen}
        onClose={() => setChatModalOpen(false)}
        touchpoint={chatTouchpoint}
        sessions={chatSessions}
        loading={chatLoading}
        pagination={chatPagination}
        onPageChange={handleChatPageChange}
        onSessionClick={openChatDetail}
      />

      {/* Chat Detail Modal */}
      <ChatDetailModal
        isOpen={chatDetailModalOpen}
        onClose={() => setChatDetailModalOpen(false)}
        session={selectedChatSession}
        loading={chatDetailsLoading}
        messages={chatMessages}
      />

      {/* App User Detail Modal */}
      <AppUserDetailModal
        isOpen={appUserDetailModalOpen}
        onClose={() => setAppUserDetailModalOpen(false)}
        user={selectedAppUser}
        gsaData={selectedAppUserGSA}
        sessions={selectedAppUserSessions}
        loading={appUserDetailLoading}
        onSessionClick={handleAppSessionRowClick}
      />

      {/* App Session Detail Modal */}
      <AppSessionDetailModal
        isOpen={appSessionDetailModalOpen}
        onClose={() => setAppSessionDetailModalOpen(false)}
        session={selectedAppSession}
        sets={selectedAppSessionSets}
        chat={selectedAppSessionChat}
        loading={appSessionDetailLoading}
      />

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar { width: 8px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #94a3b8; border-radius: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #64748b; }
      `}</style>
    </div>
  )
}

export default AnalyticsPage